import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { dataService } from '../../services/dataService'
import { Holiday } from '../../types'
import { Modal } from '../ui/Modal'
import { Plus, EditIcon, Trash2 } from '../icons/Icons'
import s from './shared.module.css'

export default function Holidays() {
  const { organization } = useAuth()
  const { holidays, refreshData } = useAppData()
  const orgId = organization!.id

  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<Holiday | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [form, setForm] = useState({ title: '', date: '' })

  const openAdd = () => { setEditing(null); setForm({ title: '', date: '' }); setSaveError(''); setShowModal(true) }
  const openEdit = (h: Holiday) => { setEditing(h); setForm({ title: h.title, date: h.date }); setSaveError(''); setShowModal(true) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); if (saving) return; setSaving(true); setSaveError('')
    try {
      if (editing) await dataService.updateHoliday(editing.id, form)
      else await dataService.createHoliday(orgId, form as any)
      setShowModal(false); await refreshData()
    } catch (err: any) { setSaveError(err?.message || 'Failed to save. Please try again.') } finally { setSaving(false) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this holiday?')) return
    await dataService.deleteHoliday(id); await refreshData()
  }

  const sortedHolidays = [...holidays].sort((a, b) => a.date.localeCompare(b.date))

  return (
    <div>
      <div className={s.pageHeader}>
        <div><h2 className={s.pageTitle}>Holidays</h2><p className={s.pageSubtitle}>{holidays.length} holiday{holidays.length !== 1 ? 's' : ''} defined</p></div>
        <button className={s.primaryBtn} onClick={openAdd}><Plus size={14} /> Add Holiday</button>
      </div>

      <div className={s.card}>
        <table className={s.table}>
          <thead><tr><th>Holiday Name</th><th>Date</th><th>Day of Week</th><th>Actions</th></tr></thead>
          <tbody>
            {sortedHolidays.length === 0 && <tr><td colSpan={4} className={s.emptyState}>No holidays defined</td></tr>}
            {sortedHolidays.map(h => {
              const d = new Date(h.date + 'T12:00:00Z')
              const dayName = d.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })
              return (
                <tr key={h.id}>
                  <td style={{ fontWeight: 600 }}>{h.title}</td>
                  <td>{h.date}</td>
                  <td>{dayName}</td>
                  <td><div className={s.actions}>
                    <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEdit(h)}><EditIcon size={14} /></button>
                    <button className={`${s.actionBtn} ${s.del}`} onClick={() => handleDelete(h.id)}><Trash2 size={14} /></button>
                  </div></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <Modal open={showModal} title={editing ? 'Edit Holiday' : 'Add Holiday'} onClose={() => setShowModal(false)}
        footer={<><button className={s.secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button><button className={s.primaryBtn} form="holidayForm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></>}>
        <form id="holidayForm" onSubmit={handleSubmit}>
          {saveError && <div className={s.formError}>{saveError}</div>}
          <div className={s.formGroup}><label>Holiday Name *</label><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required /></div>
          <div className={s.formGroup}><label>Date *</label><input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required /></div>
        </form>
      </Modal>
    </div>
  )
}
